import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import {
    CustomizerContainer,
    Section,
    SectionTitle,
    Row,
    Column,
    Label,
    Select,
    SelectWrapper,
    TabGroup,
    Tab
} from './styles/WidgetCustomizer.styles';

import Modal from './components/Modal/Modal';
import { LayoutTab, ColorsTab, SettingsTab } from './components/Tabs';
import { PLAYER_PRESETS } from '../../constants/playerPresets';
import { useStationConfig } from '../../hooks/useStationConfig';
import Notification from '../Notification/Notification';

function WidgetCustomizer({ onStateChange }) {
    const [activeTab, setActiveTab] = useState('layout');
    const [style, setStyle] = useState('vertical');
    const [playerType, setPlayerType] = useState('widget');
    const [device, setDevice] = useState('desktop');
    const [textAlign, setTextAlign] = useState('center');
    const [buttonAlign, setButtonAlign] = useState('center');
    const [albumArt, setAlbumArt] = useState('cover');
    const [fontFamily, setFontFamily] = useState('poppins');
    const [fontSize, setFontSize] = useState({ value: 1, unit: 'rem' });
    const [fontWeight, setFontWeight] = useState('400');
    const [fontSpacing, setFontSpacing] = useState({
        letterSpacing: { value: 0, unit: 'px' },
        lineHeight: { value: 1.5, unit: 'em' },
        wordSpacing: { value: 0, unit: 'px' }
    });
    const [buttonStyle, setButtonStyle] = useState('circle');
    const [colors, setColors] = useState(PLAYER_PRESETS.vertical.colors);
    const [settings, setSettings] = useState({
        volumeControl: true,
        songHistory: true,
        songName: true,
        artistName: true,
        time: true,
        timeBar: true,
        radioName: true,
        liveIndicator: true
    });
    const [dimensions, setDimensions] = useState({
        width: '300px',
        height: 'auto',
        borderSize: '0px',
        borderRadius: '8px',
        padding: '15px',
        margin: '10px'
    });

    const [deviceVisibility, setDeviceVisibility] = useState({
        mobile: false,
        tablet: false,
        desktop: false
    });

    // Use the station configuration hook
    const {
        urls,
        setUrls,
        error,
        isLoading,
        notification,
        closeNotification,
        contactSupport
    } = useStationConfig();

    const [helpModal, setHelpModal] = useState({ isOpen: false, title: '', content: '' });

    const handleStyleChange = (newStyle) => {
        const preset = PLAYER_PRESETS[newStyle];
        setStyle(newStyle);

        // Only update colors that haven't been customized
        setColors(prevColors => {
            const newColors = { ...preset.colors };
            // Preserve any customized colors
            Object.keys(prevColors).forEach(key => {
                if (prevColors[key] !== PLAYER_PRESETS[style].colors[key]) {
                    newColors[key] = prevColors[key];
                }
            });
            return newColors;
        });

        // Only update settings that haven't been customized
        setSettings(prevSettings => {
            const newSettings = { ...preset.settings };
            // Preserve any customized settings
            Object.keys(prevSettings).forEach(key => {
                if (prevSettings[key] !== PLAYER_PRESETS[style].settings[key]) {
                    newSettings[key] = prevSettings[key];
                }
            });
            return newSettings;
        });

        // Only update dimensions that haven't been customized
        setDimensions(prevDimensions => {
            const newDimensions = { ...preset.dimensions };
            // Preserve any customized dimensions
            Object.keys(prevDimensions).forEach(key => {
                if (prevDimensions[key] !== PLAYER_PRESETS[style].dimensions[key]) {
                    newDimensions[key] = prevDimensions[key];
                }
            });
            return newDimensions;
        });

        // Preserve customized text alignment
        if (textAlign !== PLAYER_PRESETS[style].textAlign) {
            // Keep current textAlign if it was customized
            setTextAlign(prevAlign => prevAlign);
        } else {
            setTextAlign(preset.textAlign);
        }

        // Preserve customized album art setting
        if (albumArt !== PLAYER_PRESETS[style].albumArt) {
            // Keep current albumArt if it was customized
            setAlbumArt(prevArt => prevArt);
        } else {
            setAlbumArt(preset.albumArt);
        }

        // Preserve customized font settings
        if (fontFamily !== PLAYER_PRESETS[style].fontFamily) {
            setFontFamily(prevFont => prevFont);
        } else {
            setFontFamily(preset.fontFamily || 'poppins');
        }

        if (fontSize.value !== (preset.fontSize?.value || 1) || fontSize.unit !== (preset.fontSize?.unit || 'rem')) {
            setFontSize(prevSize => prevSize);
        } else {
            setFontSize(preset.fontSize || { value: 1, unit: 'rem' });
        }

        if (fontWeight !== PLAYER_PRESETS[style].fontWeight) {
            setFontWeight(prevWeight => prevWeight);
        } else {
            setFontWeight(preset.fontWeight || '400');
        }

        // Preserve customized font spacing
        if (fontSpacing !== PLAYER_PRESETS[style].fontSpacing) {
            setFontSpacing(prevSpacing => prevSpacing);
        } else {
            setFontSpacing(preset.fontSpacing || {
                letterSpacing: { value: 0, unit: 'px' },
                lineHeight: { value: 1.5, unit: 'em' },
                wordSpacing: { value: 0, unit: 'px' }
            });
        }

        // Preserve customized button settings
        if (buttonStyle !== PLAYER_PRESETS[style].buttonStyle) {
            setButtonStyle(prevStyle => prevStyle);
        } else {
            setButtonStyle(preset.buttonStyle || 'circle');
        }

        if (buttonAlign !== PLAYER_PRESETS[style].buttonAlign) {
            setButtonAlign(prevAlign => prevAlign);
        } else {
            setButtonAlign(preset.buttonAlign || 'center');
        }
    };

    useEffect(() => {
        onStateChange({
            style,
            playerType,
            device,
            textAlign,
            buttonAlign,
            albumArt,
            fontFamily,
            fontSize,
            fontWeight,
            fontSpacing,
            buttonStyle,
            colors,
            settings,
            dimensions,
            urls,
            deviceVisibility
        });
    }, [style, playerType, device, textAlign, buttonAlign, albumArt, fontFamily, fontSize, fontWeight, fontSpacing, buttonStyle, colors, settings, dimensions, urls, deviceVisibility]);

    const showHelp = (title, content) => {
        setHelpModal({
            isOpen: true,
            title,
            content
        });
    };

    const closeHelp = () => {
        setHelpModal({
            isOpen: false,
            title: '',
            content: ''
        });
    };

    return (
        <CustomizerContainer>
            {helpModal.isOpen && (
                <Modal
                    title={helpModal.title}
                    content={helpModal.content}
                    onClose={closeHelp}
                />
            )}
            <Notification
                isOpen={notification.isOpen}
                onClose={closeNotification}
                title={notification.title}
                message={notification.message}
                primaryAction={{
                    label: 'Contact Support',
                    onClick: contactSupport
                }}
            />
            <Section>
                <SectionTitle>Customize</SectionTitle>

                <Row>
                    <Column>
                        <Label>Player type</Label>
                        <SelectWrapper>
                            <Select value={playerType} onChange={(e) => setPlayerType(e.target.value)}>
                                <option value="widget">Widget</option>
                                <option value="sticky">Sticky</option>
                            </Select>
                            <FontAwesomeIcon icon={faCaretDown} className="select-icon" />
                        </SelectWrapper>
                    </Column>
                    <Column>
                        <Label>Device</Label>
                        <SelectWrapper>
                            <Select value={device} onChange={(e) => setDevice(e.target.value)}>
                                <option value="desktop">Desktop</option>
                                <option value="mobile">Mobile</option>
                            </Select>
                            <FontAwesomeIcon icon={faCaretDown} className="select-icon" />
                        </SelectWrapper>
                    </Column>
                </Row>

                <Row>
                    <TabGroup>
                        <Tab active={activeTab === 'layout'} onClick={() => setActiveTab('layout')}>
                            Layout
                        </Tab>
                        <Tab active={activeTab === 'colors'} onClick={() => setActiveTab('colors')}>
                            Colors
                        </Tab>
                        <Tab active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
                            Settings
                        </Tab>
                    </TabGroup>
                </Row>

                {activeTab === 'layout' && (
                    <LayoutTab
                        style={style}
                        setStyle={handleStyleChange}
                        settings={settings}
                        setSettings={setSettings}
                        textAlign={textAlign}
                        setTextAlign={setTextAlign}
                        buttonAlign={buttonAlign}
                        setButtonAlign={setButtonAlign}
                        albumArt={albumArt}
                        setAlbumArt={setAlbumArt}
                        fontFamily={fontFamily}
                        setFontFamily={setFontFamily}
                        fontSize={fontSize}
                        setFontSize={setFontSize}
                        fontWeight={fontWeight}
                        setFontWeight={setFontWeight}
                        fontSpacing={fontSpacing}
                        setFontSpacing={setFontSpacing}
                        buttonStyle={buttonStyle}
                        setButtonStyle={setButtonStyle}
                        dimensions={dimensions}
                        setDimensions={setDimensions}
                        playerType={playerType}
                    />
                )}

                {activeTab === 'colors' && (
                    <ColorsTab
                        colors={colors}
                        setColors={setColors}
                    />
                )}

                {activeTab === 'settings' && (
                    <SettingsTab
                        urls={urls}
                        setUrls={setUrls}
                        deviceVisibility={deviceVisibility}
                        setDeviceVisibility={setDeviceVisibility}
                        showHelp={showHelp}
                        error={error}
                        settings={settings}
                        setSettings={setSettings}
                    />
                )}
            </Section>
        </CustomizerContainer>
    );
}

export default WidgetCustomizer; 